import { create } from "zustand";
import { immer } from "zustand/middleware/immer";

export interface Pair {
  name: string;
  symbol: string;
  decimals: number;
  imageUrl: string;
  pairIndex: number;
  indexPrice: number;
  listedDate: string;
  decimalsToShow: number;
  group: number;
  chartName: string;
  groupName: string;
  maxOpenInterest: string;
  maxLeverage: number;
  isFuture?: boolean;
  historyCall?: string;
  nameDisplay: string;
  isClose?: boolean;
  isNew?: boolean;
  isDevelopment?: boolean;
  isNoSupport24h?: boolean;
  minLeverage?: number | undefined;
  isHidden?: boolean;
}

export enum HistoryCallType {
  okx = "okx",
  holdstation = "holdstation",
}

export interface PairsState {
  data: Pair[];
}

const pairs = [
  // ================================
  // 🪙 CRYPTO
  // ================================
  {
    name: "Bitcoin",
    symbol: "BTCUSDT",
    nameDisplay: "BTCUSD",
    decimals: 8,
    imageUrl: require("@/assets/pairs/BTCUSDT.png"),
    pairIndex: 0,
    indexPrice: 0,
    listedDate: "1689662312000",
    decimalsToShow: 2,
    group: 0,
    chartName: "BINANCE:BTCUSDT",
    groupName: "crypto",
    maxOpenInterest: "100000000000000000000000",
    maxLeverage: 100,
  },
  {
    name: "Ethereum",
    symbol: "ETHUSDT",
    nameDisplay: "ETHUSD",
    decimals: 18,
    isNative: true,
    imageUrl: require("@/assets/pairs/ETHUSDT.png"),
    pairIndex: 1,
    indexPrice: 1,
    listedDate: "1689662312000",
    decimalsToShow: 2,
    group: 0,
    chartName: "BINANCE:ETHUSDT",
    groupName: "crypto",
    maxOpenInterest: "100000000000000000000000",
    maxLeverage: 100,
  },
  {
    name: "Worldcoin",
    symbol: "WLDUSDT",
    nameDisplay: "WLDUSD",
    decimals: 18,
    imageUrl: require("@/assets/pairs/WLDUSDT.png"),
    pairIndex: 22,
    indexPrice: 22,
    listedDate: "1738945879000",
    decimalsToShow: 4,
    group: 0,
    chartName: "BINANCE:WLDUSDT",
    groupName: "crypto",
    maxOpenInterest: "100000000000000000000000",
    maxLeverage: 50,
  },
  {
    name: "Optimism",
    symbol: "OPUSDT",
    nameDisplay: "OPUSD",
    decimals: 18,
    imageUrl: require("@/assets/pairs/OPUSDT.png"),
    pairIndex: 91,
    indexPrice: 91,
    listedDate: "1748593972000",
    decimalsToShow: 4,
    group: 0,
    chartName: "BINANCE:OPUSDT",
    groupName: "crypto",
    maxOpenInterest: "100000000000000000000000",
    maxLeverage: 50,
  },
  {
    name: "Tether Gold",
    symbol: "XAUTUSDT",
    nameDisplay: "XAUTUSD",
    decimals: 18,
    imageUrl: require("@/assets/pairs/XAUTUSDT.png"),
    pairIndex: 92,
    indexPrice: 92,
    listedDate: "1748593972000",
    decimalsToShow: 2,
    historyCall: "okx",
    group: 0,
    chartName: "OKX:XAUTUSDT",
    groupName: "crypto",
    maxOpenInterest: "100000000000000000000000",
    maxLeverage: 150,
  },
  {
    name: "Solana",
    symbol: "SOLUSDT",
    nameDisplay: "SOLUSD",
    decimals: 18,
    imageUrl: require("@/assets/pairs/SOLUSDT.png"),
    pairIndex: 26,
    indexPrice: 26,
    listedDate: "1689751500000",
    decimalsToShow: 2,
    group: 0,
    chartName: "BINANCE:SOLUSDT",
    groupName: "crypto",
    maxOpenInterest: "100000000000",
    maxLeverage: 100,
    isDevelopment: true,
  },
  {
    name: "Binance Coin",
    symbol: "BNBUSDT",
    nameDisplay: "BNBUSD",
    decimals: 18,
    imageUrl: require("@/assets/pairs/BNBUSDT.png"),
    pairIndex: 10,
    indexPrice: 10,
    listedDate: "1689662312000",
    decimalsToShow: 3,
    group: 0,
    chartName: "BINANCE:BNBUSDT",
    groupName: "crypto",
    maxOpenInterest: "100000000000",
    maxLeverage: 100,
  },

  // ================================
  // 🛢 COMMODITIES
  // ================================
  {
    name: "XAU",
    symbol: "XAUUSD",
    nameDisplay: "XAUUSD",
    decimals: 18,
    imageUrl: require("@/assets/pairs/XAUUSD.png"),
    pairIndex: 2,
    indexPrice: 2,
    listedDate: "1753418155000",
    decimalsToShow: 2,
    historyCall: "holdstation",
    group: 2,
    chartName: "BINANCE:XAUUSD",
    groupName: "commodities-1",
    maxOpenInterest: "10000000000000000000000000",
    maxLeverage: 250,
    isNew: true,
    isNoSupport24h: true,
  },
  {
    name: "OIL",
    symbol: "OILUSD",
    nameDisplay: "OILUSD",
    decimals: 18,
    imageUrl: require("@/assets/pairs/OILUSD.png"),
    pairIndex: 19,
    indexPrice: 19,
    listedDate: "1753418152000",
    decimalsToShow: 2,
    historyCall: "holdstation",
    group: 2,
    chartName: "BINANCE:OILUSD",
    groupName: "commodities-1",
    maxOpenInterest: "10000000000000000000000000",
    maxLeverage: 250,
    isNew: true,
    isNoSupport24h: true,
  },

  // ================================
  // 💱 FOREX
  // ================================
  {
    name: "GBPJPY",
    symbol: "GBPJPY",
    nameDisplay: "GBPJPY",
    decimals: 18,
    imageUrl: require("@/assets/pairs/GBPJPY.png"),
    pairIndex: 3,
    indexPrice: 3,
    listedDate: "1689662312000",
    decimalsToShow: 2,
    group: 1,
    chartName: "FOREXCOM:GBPJPY",
    maxLeverage: 500,
    minLeverage: 10,
    groupName: "forex",
    maxOpenInterest: "80000000000000000",
    isNew: true,
    isNoSupport24h: true,
  },
  {
    name: "GBPUSD",
    symbol: "GBPUSD",
    nameDisplay: "GBPUSD",
    decimals: 18,
    imageUrl: require("@/assets/pairs/GBPUSD.png"),
    pairIndex: 5,
    indexPrice: 5,
    listedDate: "1689662312000",
    decimalsToShow: 5,
    maxLeverage: 500,
    minLeverage: 10,
    group: 1,
    chartName: "FOREXCOM:GBPUSD",
    groupName: "forex",
    maxOpenInterest: "80000000000000000",
    isNew: true,
    isNoSupport24h: true,
  },
  {
    name: "EURUSD",
    symbol: "EURUSD",
    nameDisplay: "EURUSD",
    decimals: 18,
    imageUrl: require("@/assets/pairs/EURUSD.png"),
    pairIndex: 4,
    indexPrice: 4,
    listedDate: "1689662312000",
    decimalsToShow: 5,
    maxLeverage: 500,
    minLeverage: 10,
    group: 1,
    chartName: "FOREXCOM:EURUSD",
    groupName: "forex",
    maxOpenInterest: "80000000000000000",
    isNew: true,
    isNoSupport24h: true,
  },

  // ================================
  // 📈 STOCK
  // ================================
  {
    name: "AAPL",
    symbol: "AAPLUSD",
    nameDisplay: "AAPLUSD",
    decimals: 8,
    imageUrl: require("@/assets/pairs/apple--big.svg"),
    pairIndex: 93,
    indexPrice: 93,
    listedDate: "1755734400000",
    decimalsToShow: 2,
    group: 5,
    chartName: "AAPLUSD:AAPLUSD",
    groupName: "stock",
    historyCall: "holdstation",
    maxOpenInterest: "50000000000000000000",
    maxLeverage: 50,
    isDevelopment: true,
  },
  {
    name: "NFLX",
    symbol: "NFLXUSD",
    nameDisplay: "NFLXUSD",
    decimals: 8,
    imageUrl: require("@/assets/pairs/netflix--big.svg"),
    pairIndex: 94,
    indexPrice: 94,
    listedDate: "1755734400000",
    decimalsToShow: 2,
    group: 5,
    chartName: "NFLXUSD:NFLXUSD",
    groupName: "stock",
    historyCall: "holdstation",
    maxOpenInterest: "50000000000000000000",
    maxLeverage: 50,
    isDevelopment: true,
  },
  {
    name: "GOOGL",
    symbol: "GOOGLUSD",
    nameDisplay: "GOOGLUSD",
    decimals: 8,
    imageUrl: require("@/assets/pairs/alphabet--big.svg"),
    pairIndex: 95,
    indexPrice: 95,
    listedDate: "1755734400000",
    decimalsToShow: 2,
    group: 5,
    chartName: "GOOGLUSD:GOOGLUSD",
    groupName: "stock",
    historyCall: "holdstation",
    maxOpenInterest: "50000000000000000000",
    maxLeverage: 50,
    isDevelopment: true,
  },
  {
    name: "TSLA",
    symbol: "TSLAUSD",
    nameDisplay: "TSLAUSD",
    decimals: 8,
    imageUrl: require("@/assets/pairs/tesla--big.svg"),
    pairIndex: 96,
    indexPrice: 96,
    listedDate: "1755734400000",
    decimalsToShow: 2,
    group: 5,
    chartName: "TSLAUSD:TSLAUSD",
    groupName: "stock",
    historyCall: "holdstation",
    maxOpenInterest: "50000000000000000000",
    maxLeverage: 50,
    isDevelopment: true,
  },

  // PAIR CHART LITE
  {
    name: "BERACHAIN",
    symbol: "BERAUSDT",
    nameDisplay: "BERAUSD",
    decimals: 18,
    isNative: true,
    imageUrl: require("@/assets/pairs/BERAUSDT.png"),
    pairIndex: 90,
    indexPrice: 90,
    listedDate: "1738945879000",
    decimalsToShow: 3,
    group: 0,
    maxLeverage: 50,

    chartName: "BINANCE:BERAUSDT",
    groupName: "crypto",
    maxOpenInterest: "50000000000",
    isHidden: true,
  },
  {
    name: "ZKsync",
    symbol: "ZKUSDT",
    nameDisplay: "ZKUSD",
    decimals: 18,
    imageUrl: require("@/assets/pairs/ZKUSD.png"),
    pairIndex: 62,
    indexPrice: 62,
    listedDate: "1689662312000",
    decimalsToShow: 4,
    maxLeverage: 50,
    group: 0,
    chartName: "BINANCE:ZKUSDT",
    groupName: "crypto",
    maxOpenInterest: "15000000000",
    isHidden: true,
  },

  {
    name: "Bittensor",
    symbol: "TAOUSDT",
    nameDisplay: "TAOUSD",
    decimals: 18,
    imageUrl: require("@/assets/pairs/TAOUSD.png"),
    pairIndex: 70,
    indexPrice: 70,
    listedDate: "1738945879000",
    decimalsToShow: 1,
    group: 0,
    chartName: "BINANCE:TAOUSDT",
    groupName: "crypto",
    maxOpenInterest: "15000000000000000000000",
    maxLeverage: 50,
    isHidden: true,
  },

  {
    name: "aixbt by Virtuals",
    symbol: "AIXBTUSDT",
    nameDisplay: "AIXBTUSD",
    decimals: 18,
    isNative: true,
    imageUrl: require("@/assets/pairs/AIXBTUSDT.png"),

    pairIndex: 84,
    indexPrice: 84,
    listedDate: "1738945879000",
    decimalsToShow: 4,
    group: 0,
    chartName: "BINANCE:AIXBTUSDT",
    groupName: "crypto",
    maxLeverage: 50,
    maxOpenInterest: "15000000000000000000000",
    isHidden: true,
  },

  {
    name: "Cookie DAO",
    symbol: "COOKIEUSDT",
    nameDisplay: "COOKIEUSD",
    decimals: 18,
    imageUrl: require("@/assets/pairs/COOKIEUSDT.png"),

    pairIndex: 85,
    indexPrice: 85,
    listedDate: "1738945879000",
    decimalsToShow: 4,
    group: 0,
    chartName: "BINANCE:COOKIEUSDT",
    groupName: "crypto",
    maxLeverage: 50,
    maxOpenInterest: "15000000000000000000000",
    isHidden: true,
  },

  {
    name: "ChainGPT",
    symbol: "CGPTUSDT",
    nameDisplay: "CGPTUSD",
    decimals: 18,
    imageUrl: require("@/assets/pairs/CGPTUSDT.png"),

    pairIndex: 86,
    indexPrice: 86,
    listedDate: "1738945879000",
    decimalsToShow: 4,
    group: 0,
    chartName: "BINANCE:CGPTUSDT",
    groupName: "crypto",
    maxLeverage: 50,
    maxOpenInterest: "15000000000000000000000",
    isHidden: true,
  },

  {
    name: "OFFICIAL TRUMP",
    symbol: "TRUMPUSDT",
    nameDisplay: "TRUMPUSD",
    decimals: 18,
    imageUrl: require("@/assets/pairs/TRUMPUSDT.png"),
    pairIndex: 87,
    indexPrice: 87,
    listedDate: "1738945879000",
    decimalsToShow: 2,
    group: 0,
    chartName: "BINANCE:TRUMPUSDT",
    groupName: "crypto",
    maxLeverage: 50,
    maxOpenInterest: "15000000000000000000000",
    isHidden: true,
  },
  {
    name: "ai16z",
    symbol: "AI16ZUSDT",
    nameDisplay: "AI16ZUSD",
    decimals: 18,
    imageUrl: require("@/assets/pairs/AI16ZUSDT.png"),
    pairIndex: 89,
    indexPrice: 89,
    listedDate: "1738945879000",
    decimalsToShow: 4,
    group: 0,
    chartName: "BINANCE:AI16ZUSDT",
    groupName: "crypto",
    maxLeverage: 50,
    maxOpenInterest: "15000000000000000000000",
    isFuture: true,
    isHidden: true,
  },
];

export const usePairs = create(
  immer<{ data: Pair[] }>(() => ({
    data: pairs,
  }))
);
